/**
 * Exceptions & escalations — the block a Dealer Principal and a Sales Manager
 * both open the page for.
 *
 * "There should be escalating where it goes to a sales manager — hey, this
 * lead has not been actioned for 24 hours, or no task is assigned for this."
 * Named in the September showcase as a gap even the incumbent product has.
 *
 * Two modes:
 *   summary   — counts by kind, each a link to the filtered lead board (DP)
 *   actionable — the top N items with inline Assign / Hand to AI / Set next
 *                step, so a manager fixes it here rather than four screens away
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import AssignConsultant from '../AssignConsultant';
import NextEventModal from '../NextEventModal';
import { leadsService } from '../../lib/services';
import { useToast } from '../../lib/toast';

const KIND_META = {
  FIRST_RESPONSE_BREACH: { label: 'Inbound not actioned',   color: 'var(--danger)',  to: '/leads?filter=unactioned' },
  UNACTIONED:            { label: 'No activity',            color: 'var(--danger)',  to: '/leads?filter=unactioned' },
  UNASSIGNED:            { label: 'Unassigned',             color: 'var(--danger)',  to: '/leads?filter=unassigned' },
  NO_NEXT_EVENT:         { label: 'No next step',           color: 'var(--warning)', to: '/leads?filter=no-next-step' },
  NURTURE_EXPIRING:      { label: 'AI nurture expiring',    color: 'var(--info)',    to: '/leads?filter=nurture-expiring' },
};

const kindMeta = (k) => KIND_META[k] || { label: String(k || '').replace(/_/g, ' '), color: 'var(--text-muted)', to: '/leads' };

const hoursLabel = (h) => {
  if (h == null) return '';
  if (h < 24) return `${Math.round(h)}h`;
  return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
};

export function ExceptionSummary({ exceptions }) {
  if (!exceptions) {
    return (
      <div className="mono text-xs text-muted">
        Exception tracking needs the role-snapshot endpoint — not available yet.
      </div>
    );
  }
  const rows = [
    ['UNACTIONED',       exceptions.unactionedLeads],
    ['UNASSIGNED',       exceptions.unassignedLeads],
    ['NO_NEXT_EVENT',    exceptions.leadsWithoutNextEvent],
    ['NURTURE_EXPIRING', exceptions.aiNurtureExpiringSoon],
  ].filter(([, n]) => (n ?? 0) > 0);

  if (!rows.length) {
    return (
      <div className="empty-state" style={{ padding: '16px 8px' }}>
        <div className="title" style={{ color: 'var(--success)' }}>Nothing needs escalating</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map(([kind, n], i) => {
        const m = kindMeta(kind);
        return (
          <Link
            key={kind}
            to={m.to}
            className="flex-between"
            style={{
              padding: '10px 0', textDecoration: 'none', color: 'inherit',
              borderBottom: i === rows.length - 1 ? 'none' : '1px solid var(--border-subtle)',
            }}
          >
            <span className="flex-row" style={{ alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, display: 'inline-block' }} />
              <span style={{ fontSize: 13 }}>{m.label}</span>
            </span>
            <span className="mono" style={{ fontWeight: 700, color: m.color }}>{n}</span>
          </Link>
        );
      })}
      {exceptions.overdueTasks > 0 && (
        <div className="mono text-xs text-muted" style={{ marginTop: 8 }}>
          + {exceptions.overdueTasks} overdue task{exceptions.overdueTasks === 1 ? '' : 's'} across the team
        </div>
      )}
    </div>
  );
}

export function ExceptionQueue({ exceptions, onChanged, limit = 8 }) {
  const { toast } = useToast();
  const [settingFor, setSettingFor] = useState(null);
  const [busyId, setBusyId] = useState(null);

  if (!exceptions) return <ExceptionSummary exceptions={null} />;
  const items = (exceptions.items || []).slice(0, limit);

  if (!items.length) {
    return (
      <div className="empty-state" style={{ padding: '16px 8px' }}>
        <div className="title" style={{ color: 'var(--success)' }}>Queue is clear</div>
        <div className="desc">Every lead is assigned, actioned and has a next step.</div>
      </div>
    );
  }

  const handToAi = async (item) => {
    setBusyId(item.leadId);
    try {
      await leadsService.handBackToAI(item.leadId);
      toast('Handed to AI', { variant: 'success' });
      onChanged?.();
    } catch (e) {
      toast(`Could not hand to AI: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {items.map((it, i) => {
          const m = kindMeta(it.kind);
          const busy = busyId === it.leadId;
          return (
            <div
              key={`${it.kind}-${it.leadId}`}
              style={{
                padding: '10px 0',
                borderBottom: i === items.length - 1 ? 'none' : '1px solid var(--border-subtle)',
                display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="flex-row" style={{ alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <Link to={`/leads/${it.leadId}`} style={{ fontSize: 13, fontWeight: 600 }}>
                    {it.customerName || 'Lead'}
                  </Link>
                  {it.vehicleCategory && it.vehicleCategory !== 'UNKNOWN' && (
                    <span className="pill" style={{ color: it.vehicleCategory === 'NEW' ? 'var(--info)' : 'var(--success)' }}>
                      {it.vehicleCategory}
                    </span>
                  )}
                  <span className="pill" style={{ color: m.color, borderColor: m.color }}>{m.label}</span>
                </div>
                <div className="mono text-xs text-muted" style={{ marginTop: 3 }}>
                  {it.ageHours != null && `${hoursLabel(it.ageHours)} · `}
                  {it.consultantName || (it.consultantId ? 'assigned' : 'unassigned')}
                  {it.vehicle ? ` · ${it.vehicle}` : ''}
                </div>
              </div>

              <div className="flex-row" style={{ gap: 6, flexShrink: 0, alignItems: 'center' }}>
                {(it.kind === 'UNASSIGNED' || it.kind === 'UNACTIONED' || it.kind === 'FIRST_RESPONSE_BREACH') && (
                  <div style={{ minWidth: 160 }}>
                    <AssignConsultant
                      leadId={it.leadId}
                      assignedConsultantId={it.consultantId || null}
                      onChanged={() => onChanged?.()}
                    />
                  </div>
                )}
                {(it.kind === 'UNACTIONED' || it.kind === 'FIRST_RESPONSE_BREACH') && (
                  <button type="button" className="btn small" disabled={busy} onClick={() => handToAi(it)}>
                    {busy ? '…' : 'Hand to AI'}
                  </button>
                )}
                {(it.kind === 'NO_NEXT_EVENT' || it.kind === 'UNACTIONED') && (
                  <button type="button" className="btn small primary" onClick={() => setSettingFor(it)}>
                    Set next step
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <NextEventModal
        open={!!settingFor}
        leadId={settingFor?.leadId || null}
        defaultAssigneeId={settingFor?.consultantId || null}
        onClose={() => setSettingFor(null)}
        onSaved={() => { setSettingFor(null); onChanged?.(); }}
      />
    </>
  );
}
